import {
    IconModule1On1,
    IconModuleObjectives,
    IconModulePraise,
    IconModuleRequestFeedback,
    IconPlus,
    IconUserArrowOut,
    IconUsers,
} from 'featherico';
import { forEach } from 'lodash/fp';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Praise from '../../../../../../types/PraiseDto';
import { CurrentUserDto } from '../../../../../../types/UserDtos';
import { ANYTIME_FEEDBACK_REQUEST_CREATE, YOUR_COMPANY_USERS_CREATE } from '../../../../../routes';
import { getAdminRouteForNavigation } from '../../../../../routes-pairs';
import { CompanyMessageProject, getActiveMessageProjects } from '../../../../../screens/message/constants/projects';
import { Divider, Dropdown, Option, OptionLink } from '../../../../components/Dropdown';
import MessageAlert from '../../../../components/PopupAlert/MessageAlert';
import { ObjectiveCreatedAlert } from '../../../../components/PopupAlert/ObjectiveAlert';
import PraiseAlert from '../../../../components/PopupAlert/PraiseAlert';
import triggerPopupAlert from '../../../../components/PopupAlert/trigger';
import SuspenseWithErrorModal from '../../../../components/SuspenseWithErrorModal';
import { EventType, createEventBusPublisher } from '../../../../hooks/useEventBus';
import { useBooleanState } from '../../../../hooks/useToggle';
import { getAngularService } from '../../../../services/AngularService';
import { isFeatureEnabled, isFeatureEnabledForUser } from '../../../../services/Features';
import r from '../../../../services/Replacement';
import { prependAppPath } from '../../../../services/Url';
import { isAnyAdmin } from '../../../../services/User';
import AnytimeFeedbackTypeSelectModal from '../../../AnytimeFeedback/components/AnytimeFeedbackTypeSelect';
import CreateMeetingModal from '../../../Meetings/CreateMeetingModal/lazy';
import PraiseCreateEditModal from '../../../Praise/components/CreateEditModal/lazy';
import styles from './style.m.less';

type Props = {
    user: CurrentUserDto;
};

const angularServicesGetters = {
    objectiveCreate: () =>
        getAngularService(
            () =>
                import(
                    /* webpackChunkName: 'ObjectiveCreate' */ '../../../../../screens/objective/components/objective-create-button/service'
                ),
            'ObjectiveCreateButtonService'
        ),
    messageCreate: () =>
        getAngularService(
            () =>
                import(
                    /* webpackChunkName: 'MessageCreateOld' */ '../../../../../screens/message/components/message-create/dialog-service'
                ),
            'MessageCreateDialogService'
        ),
};

export const createObjective = () =>
    angularServicesGetters
        .objectiveCreate()
        .then(ObjectiveCreateButtonService =>
            ObjectiveCreateButtonService.fetchRequiredData().then(() => ObjectiveCreateButtonService)
        )
        .then(ObjectiveCreateButtonService => {
            ObjectiveCreateButtonService.create().then(
                (objective: any) =>
                    triggerPopupAlert(<ObjectiveCreatedAlert objective={objective} />, 'success', { timeoutMs: null }),
                () => {} // dialog was closed
            );
        });

const createMessageForProject = (project: CompanyMessageProject) => () =>
    angularServicesGetters.messageCreate().then(MessageCreateDialogService => {
        MessageCreateDialogService.show(project).dialog.promise.then(
            (message: any) =>
                triggerPopupAlert(
                    <MessageAlert entityName={project.entityName} project={project} message={message} />,
                    'success',
                    { timeoutMs: null }
                ),
            () => {} // dialog was closed
        );
    });

const usePreload = () => {
    const [preloadTriggered, setPreloadTriggered] = useState(false);

    const onTriggerPreload = useCallback(() => {
        if (!preloadTriggered) {
            setPreloadTriggered(true);
        }
    }, [preloadTriggered]);

    useEffect(() => {
        if (preloadTriggered) {
            forEach(getter => getter(), angularServicesGetters);
        }
    }, [preloadTriggered]);

    return { preloadTriggered, onTriggerPreload };
};

const onPraiseCreateSuccess = (praise: Praise) =>
    triggerPopupAlert(<PraiseAlert praise={praise} />, 'success', { timeoutMs: null });
const meetingCreatedPublisher = createEventBusPublisher(EventType.MeetingCreated);

const GlobalCreate: React.FC<Props> = ({ user }) => {
    const { preloadTriggered, onTriggerPreload } = usePreload();
    const [praiseModalOpen, openPraiseModal, closePraiseModal] = useBooleanState();
    const [meetingModalOpen, openMeetingModal, closeMeetingModal] = useBooleanState();
    const [anytimeFeedbackModalOpen, openAnytimeFeedbackModal, closeAnytimeFeedbackModal] = useBooleanState();
    const messageProjects = useMemo(() => getActiveMessageProjects(user), [user]);

    return (
        <>
            <div className={styles.globalCreate} onMouseOver={onTriggerPreload}>
                <Dropdown
                    showChevron={false}
                    label={
                        <>
                            <IconPlus />
                            {!isFeatureEnabled('FEATURE_layoutRefresh') && (
                                <>
                                    {' '}
                                    <span className="app-header-item-label">Create</span>
                                </>
                            )}
                        </>
                    }
                    eventTracking={{ category: 'navigation header', action: 'click create' }}
                    large={true}
                    className={isFeatureEnabled('FEATURE_layoutRefresh') && 'top-aligned left-aligned'}
                    kind={isFeatureEnabled('FEATURE_layoutRefresh') ? 'PLAIN' : 'DEFAULT'}
                >
                    {isFeatureEnabledForUser('praise', user) && (
                        <Option
                            onClick={() => {
                                onTriggerPreload();
                                openPraiseModal();
                            }}
                            eventTracking={{ category: 'navigation header', action: 'create', label: 'praise' }}
                        >
                            <IconModulePraise /> {r('praise.header')}
                        </Option>
                    )}

                    {isFeatureEnabledForUser('meetings', user) && (
                        <Option
                            onClick={() => {
                                onTriggerPreload();
                                openMeetingModal();
                            }}
                            eventTracking={{ category: 'navigation header', action: 'create', label: 'meeting' }}
                        >
                            <IconModule1On1 /> {r('meeting.name')}
                        </Option>
                    )}
                    {isFeatureEnabledForUser('objective', user) && (
                        <Option
                            onClick={createObjective}
                            eventTracking={{ category: 'navigation header', action: 'create', label: 'objective' }}
                        >
                            <IconModuleObjectives /> {r('objective.singular.big')}
                        </Option>
                    )}
                    {isFeatureEnabledForUser('requestFeedback', user) &&
                        (isFeatureEnabledForUser('FEATURE_anytimeFeedbackGive', user) ? (
                            <Option
                                onClick={() => {
                                    onTriggerPreload();
                                    openAnytimeFeedbackModal();
                                }}
                                eventTracking={{
                                    category: 'navigation header',
                                    action: 'create',
                                    label: 'anytime feedback',
                                }}
                            >
                                <IconUserArrowOut /> {r('anyfb.name')}
                            </Option>
                        ) : (
                            <OptionLink
                                href={prependAppPath(ANYTIME_FEEDBACK_REQUEST_CREATE)}
                                eventTracking={{
                                    category: 'navigation header',
                                    action: 'create',
                                    label: 'feedback request',
                                }}
                            >
                                <IconModuleRequestFeedback /> Feedback Request
                            </OptionLink>
                        ))}

                    {isFeatureEnabledForUser('message', user) &&
                        messageProjects.map(project => (
                            <Option
                                key={project.id}
                                onClick={createMessageForProject(project)}
                                eventTracking={{
                                    category: 'navigation header',
                                    action: 'create',
                                    label: project.entityName.toLocaleLowerCase(),
                                }}
                            >
                                <project.iconComponent /> {project.entityName}
                            </Option>
                        ))}
                    {isAnyAdmin(user) && (
                        <>
                            <Divider />
                            <OptionLink
                                href={prependAppPath(getAdminRouteForNavigation(YOUR_COMPANY_USERS_CREATE))}
                                eventTracking={{ category: 'navigation header', action: 'create', label: 'users' }}
                            >
                                <IconUsers /> Users
                            </OptionLink>
                        </>
                    )}
                </Dropdown>
            </div>
            <SuspenseWithErrorModal enableErrorModal={meetingModalOpen}>
                {preloadTriggered && (
                    <CreateMeetingModal
                        isOpen={meetingModalOpen}
                        onClose={closeMeetingModal}
                        onCreate={meetingCreatedPublisher}
                        isDraft={false}
                    />
                )}
            </SuspenseWithErrorModal>

            <SuspenseWithErrorModal enableErrorModal={praiseModalOpen}>
                {preloadTriggered && (
                    <PraiseCreateEditModal
                        isOpen={praiseModalOpen}
                        onClose={closePraiseModal}
                        onSuccess={onPraiseCreateSuccess}
                    />
                )}
            </SuspenseWithErrorModal>

            <SuspenseWithErrorModal enableErrorModal={anytimeFeedbackModalOpen}>
                {preloadTriggered && (
                    <AnytimeFeedbackTypeSelectModal
                        isOpen={anytimeFeedbackModalOpen}
                        onClose={closeAnytimeFeedbackModal}
                    />
                )}
            </SuspenseWithErrorModal>
        </>
    );
};

export default GlobalCreate;

if (import.meta.webpackHot) {
    import.meta.webpackHot.decline([
        '../../../../../screens/objective/components/objective-create-button/service',
        '../../../../../screens/message/components/message-create/dialog-service',
    ]);
}
